'use client';

import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { type ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import { customerReviewsInfoCallToActionMessages } from './CustomerReviewsInfoCallToAction.messages';
import { useCustomerReviewsTracking } from '../Tracking/useCustomerReviewsTracking/useCustomerReviewsTracking';

export type CustomerReviewsInfoCallToActionProps = {
  /** Dialog heading; blank or missing, the translated default. */
  title?: string;
  /** Hint text, rendered by the server component that owns the spot; without it the legacy servicelayer. */
  content?: ReactNode;
};

/**
 * CustomerReviewsInfoCallToAction button and dialog with rating information
 */
export const CustomerReviewsInfoCallToAction = ({
  title,
  content,
}: CustomerReviewsInfoCallToActionProps) => {
  const {
    product: {
      reviews: { hintLayerId },
    },
  } = useConfig();

  const [dialogOpened, setDialogOpened] = useState(false);
  const { formatMessage } = useIntl();
  const { trackInfoDisplayOverlay } = useCustomerReviewsTracking();

  // Tracking reports the heading the visitor saw, not the default.
  const dialogTitle = title?.trim() || formatMessage(customerReviewsInfoCallToActionMessages.title);

  const handleDialogOpen = () => {
    trackInfoDisplayOverlay(dialogTitle);
    setDialogOpened(true);
  };
  const handleDialogClose = () => {
    setDialogOpened(false);
  };

  return (
    <>
      <Dialog
        isOpen={dialogOpened}
        title={dialogTitle}
        onClose={handleDialogClose}
        variant="bottomSheet"
      >
        <DialogHeader onClose={handleDialogClose}>{dialogTitle}</DialogHeader>
        <DialogContent className="max-w-screen-md">
          {content ?? <ServiceLayer serviceLayerId={hintLayerId} />}
        </DialogContent>
      </Dialog>
      <div
        role="button"
        tabIndex={0}
        onClick={handleDialogOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleDialogOpen();
          }
        }}
        aria-label={formatMessage(customerReviewsInfoCallToActionMessages.ariaLabel)}
        className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center"
      >
        <span className="icon-emp-information-circle-filled inline-block size-2" />
      </div>
    </>
  );
};
